import React, { Children, cloneElement, isValidElement, ReactNode, useCallback, useEffect, useState } from 'react'
import { LayoutRectangle } from 'react-native'
import {
  styled,
  SizableText,
  withStaticProperties,
  GetProps,
  YStack,
  AnimatePresence,
  StackProps,
  createStyledContext,
  SizeTokens,
  Tabs as TTabs,
} from '@red-ui/components'
import { useResponsiveTabText } from './useResponsiveTabText'
type TabState = {
  currentTab: string
  activeAt: LayoutRectangle | null
  prevActiveAt: LayoutRectangle | null
}

type TabProps = GetProps<typeof TabsComponent.Tab> & { 'aria-selected': boolean; 'aria-label': string }
type ListProps = GetProps<typeof TabsComponent.List>
type ContentProps = GetProps<typeof TTabs.Content> & { animated?: boolean }

interface TabSwitchWithIndicatorProps extends GetProps<typeof Tabs> {
  defaultValue?: string
  onValueChange?: (value: string) => void
  value?: string
  children: ReactNode
  indicatorStyle?: { [key: string]: string }
  renderListWrapper?: (children: ReactNode) => ReactNode
}

export const TabsContext = createStyledContext({
  size: '$3' as SizeTokens,
})

const Tabs = styled(TTabs, {
  context: TabsContext,
  orientation: 'horizontal',
  flexDirection: 'column',
})

const List = styled(TTabs.List, {
  name: 'List',
  borderWidth: '$0.25',
  borderColor: '$borderColor',
  disablePassBorderRadius: true,
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '$background',
  borderRadius: 500,
})

const Tab = styled(TTabs.Tab, {
  name: 'Tab',
  context: TabsContext,
  accessible: true,
  flex: 1,
  borderWidth: 0,
  borderRadius: 500,
  outlineColor: '$outlineColor',
  variants: {
    size: {
      '...size': (name, { tokens }) => {
        return {
          //factor up the size of the tab container for size, to match design specs as it increases.
          height: tokens.size[name as keyof typeof tokens.size].val * 1.83,
        }
      },
    },
  },
})

const TabContent = styled(TTabs.Content, {
  name: 'Content',
  context: TabsContext,
  variants: {
    animated: {
      // animated prop to enable animation which is added later
      true: {},
    },
  } as const,
})

const Text = styled(SizableText, {
  name: 'Text',
  context: TabsContext,
  fontWeight: '700',
  textAlign: 'center',
  color: '$red6',
})

const RovingTabText = styled(Text, {
  name: 'RovingTabText',
  context: TabsContext,
  color: '$background',
})

const RovingTab = styled(YStack, {
  name: 'RovingTab',
  context: TabsContext,
  role: 'tab',
  justifyContent: 'center',
  position: 'absolute',
  // Roving indicator needs a little repositioning as its not actually in the list container.
  top: 4,
  left: 4,
  borderRadius: 500,
  backgroundColor: '$red6',
  animation: 'quick',
  enterStyle: { opacity: 0 },
  exitStyle: { opacity: 0 },
})

type TabsRovingIndicatorProps = {
  activeTab: string
  indicatorStyle: { [key: string]: string }
} & StackProps

// The indicator that moves between tabs
const TabsRovingIndicator = ({ activeTab, indicatorStyle, ...props }: TabsRovingIndicatorProps) => {
  const responsiveText = useResponsiveTabText(activeTab)

  return (
    <RovingTab accessible={false} aria-hidden={true} {...props} {...indicatorStyle}>
      <RovingTabText accessible={false} enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }}>
        {responsiveText}
      </RovingTabText>
    </RovingTab>
  )
}

const TabsWithIndicator = ({
  defaultValue,
  onValueChange,
  value,
  children,
  indicatorStyle = {},
  renderListWrapper,
  ...props
}: TabSwitchWithIndicatorProps) => {
  const [tabState, setTabState] = useState<TabState>({
    activeAt: null,
    currentTab: value ?? defaultValue ?? '',
    prevActiveAt: null,
  })
  const { activeAt, currentTab } = tabState
  const [animatedContentChildren, setAnimatedContentChildren] = useState<ReactNode[]>([])
  const [contentChildren, setContentChildren] = useState<ReactNode[]>([])

  const setCurrentTab = useCallback(
    (newTab: string) => {
      setTabState((prev) => ({ ...prev, currentTab: newTab }))
      onValueChange?.(newTab)
    },
    [onValueChange, setTabState]
  )

  const getTabsContentChildren = useCallback(
    (children: ReactNode) => {
      const animatedContent: ReactNode[] = []
      const content: ReactNode[] = []

      Children.forEach(children, (child) => {
        if (isValidElement<ContentProps>(child) && child.type === TabsComponent.Content) {
          if (child.props.animated) {
            animatedContent.push(
              cloneElement<ContentProps>(child, {
                key: child.props.value,
                animation: 'lazy',
                enterStyle: { opacity: 0, y: 20 },
                exitStyle: { opacity: 0, y: -20 },
              })
            )
          } else {
            content.push(child)
          }
        }
      })

      setAnimatedContentChildren(animatedContent)
      setContentChildren(content)
    },
    [setAnimatedContentChildren, setContentChildren]
  )

  useEffect(() => {
    if (value !== undefined && value !== currentTab) {
      setCurrentTab(value)
    }
  }, [value, currentTab, setCurrentTab])

  useEffect(() => {
    getTabsContentChildren(children)
  }, [children, getTabsContentChildren])

  useEffect(() => {
    // Remove this once tamagui is upgraded again
    // Apply width: 100% to the first child of tabs-container to fix the wrapper div width issue
    const tabsContainerElement = document.getElementById('tabs-container')
    if (tabsContainerElement?.firstElementChild instanceof HTMLElement) {
      tabsContainerElement.firstElementChild.style.width = '100%'
    }
  }, [currentTab])

  const handleOnInteraction: GetProps<typeof Tab>['onInteraction'] = (type: string, layout: LayoutRectangle | null) => {
    if (type !== 'select') return
    setTabState((prev) => ({
      ...prev,
      prevActiveAt: prev.activeAt,
      activeAt: layout,
    }))
  }

  const tabChildren = Children.map(children, (child) => {
    if (isValidElement<ListProps>(child) && child.type === TabsComponent.List) {
      // Iterate over the children of the List and enhance the Tabs
      const enhancedTabs = Children.map(child.props.children, (tab) => {
        if (isValidElement<TabProps>(tab) && tab.type === TabsComponent.Tab) {
          // Modify the tab to include the onInteraction callback and aria-selected
          const isActive = tab.props.value === currentTab
          return cloneElement(tab, {
            'aria-selected': isActive,
            'aria-label': isActive ? `Current tab: ${tab.props.value}` : `Press to select tab: ${tab.props.value}`,
            active: isActive,
            onInteraction: handleOnInteraction,
          })
        }
        return tab
      })
      return cloneElement<ListProps>(child, {}, enhancedTabs)
    }
    // Exclude all TabsComponent.Content from tabChildren (regardless of animated)
    if (isValidElement(child) && child.type === TabsComponent.Content) {
      return null
    }
    return child
  })

  // Wrap tabList and indicator in a single element
  const tabsListWithIndicator = (
    <>
      {tabChildren}
      {activeAt && (
        <AnimatePresence>
          <TabsRovingIndicator
            {...props}
            indicatorStyle={indicatorStyle}
            activeTab={currentTab}
            width={activeAt.width - 4} // subtract 4 to account for the border
            height={activeAt.height - 4}
            x={activeAt.x}
            y={activeAt.y}
          />
        </AnimatePresence>
      )}
    </>
  )

  return (
    <YStack alignItems="center" id="tabs-container">
      <Tabs value={currentTab} onValueChange={setCurrentTab} {...props}>
        {renderListWrapper ? renderListWrapper(tabsListWithIndicator) : tabsListWithIndicator}
        {animatedContentChildren && animatedContentChildren.length > 0 ? (
          <AnimatePresence>{animatedContentChildren}</AnimatePresence>
        ) : (
          contentChildren
        )}
      </Tabs>
    </YStack>
  )
}

const TabsComponent = withStaticProperties(TabsWithIndicator, {
  List,
  Tab,
  Text,
  Content: TabContent,
  Props: TabsContext.Provider,
})

export type TabsProps = GetProps<typeof TabsComponent>

export { TabsComponent as Tabs }
